/**
 * Card component with Dashboard3 glass morphism styling
 *
 * Note: Must be "use client" because it uses useThemeTokens and useInteractiveTokens hooks
 *
 * @example
 * ```tsx
 * <Card>Card content</Card>
 * <Card featured>Featured card</Card>
 * ```
 */

"use client";

import { Box, forwardRef, type BoxProps } from "@chakra-ui/react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";
import { useClassicDashboardStyles } from "@/lib/hooks/useClassicDashboardStyles";

export interface CardProps extends BoxProps {
  /**
   * Whether this is a featured card (stronger border/glow)
   */
  featured?: boolean;
  bg?: string;
}

export const Card = forwardRef<CardProps, "div">(
  ({ featured = false, children, bg = "none", ...props }, ref) => {
    const theme = useThemeTokens();
    const interactive = useInteractiveTokens();
    const { cardBorderRadius, backdropBlur } = useClassicDashboardStyles();

    return (
      <Box
        ref={ref}
        borderRadius={cardBorderRadius}
        borderWidth="1px"
        borderColor={
          featured ? theme.accent.primaryBorder : theme.borderGlass.default
        }
        bg={bg}
        backdropFilter={backdropBlur ?? interactive.backdrop.blur}
        position="relative"
        overflow="hidden"
        boxShadow={featured ? theme.shadow.cardFeatured : theme.shadow.card}
        {...props}
      >
        {children}
      </Box>
    );
  }
);
